import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { scroller } from 'react-scroll';
import { FEATURE_FLAGS, formatMoney } from '@belong/common';
import { IconCheck, IconCheckboxSelected } from '@belong/icons';
import { ButtonBase, Image, Text } from '@belong/ui';
import { Confetti as ConfettiAnimation } from 'animations/Confetti/Confetti';
import classNames from 'classnames/bind';
import clsx from 'clsx';
import AddOrSubtract from 'components/AddOrSubtract/AddOrSubtract';
import Field from 'components/Field/Field';
import Form from 'components/Form/Form';
import { InputFinalFormAdapter } from 'components/Input/Input';
import TextBubbleWithUserProfile from 'components/TextBubbleWithUserProfile/TextBubbleWithUserProfile';
import Space, { SPACE_TYPES } from 'corecomponents/Space/Space';
import { Satisfaction } from 'formcomponents/satisfaction/satisfaction';
import FormLayout from 'layouts/FormLayout/FormLayout';
import PropTypes from 'prop-types';
import { showToast } from 'store/redux/toast';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import PostInspectionContentLayout from '../../../PostInspectionContentLayout/PostInspectionContentLayout';
import { getPricingAssignedEmployee } from '../utils';
import PlacementFeeModal from './PlacementFeeModal';
import styles from './InitialListing.module.css';

const cx = classNames.bind(styles);

const Banner = ({
  tagText,
  tagColor,
  variant = 'green',
  children,
  className,
}) => (
  <div
    className={clsx(
      'relative mt-2xl rounded border border-solid px-sm py-xl',
      variant === 'green' && 'border-green bg-green-translucent-light',
      variant === 'gray' && 'border-gray bg-light-gray',
      className,
    )}
  >
    <div className="absolute inset-x-0 top-0 -translate-y-1/2">
      <div
        className={`mx-auto max-w-min whitespace-pre rounded px-xs py-2xs md:max-w-max md:whitespace-normal${
          tagColor === 'navy' ? ' bg-navy' : ''
        }${tagColor === 'green' ? ' bg-green' : ''}`}
      >
        <Text
          fontWeight="semibold"
          variant="p1"
          className="text-center text-white"
        >
          {tagText}
        </Text>
      </div>
    </div>
    {children}
  </div>
);

const InitialListing = ({
  stepResource,
  stepConfigs,
  stepConfig,
  stepData,
  onSaveAndNext,
  onPrevious: _onPrevious,
}) => {
  const dispatch = useDispatch();
  const {
    initialListingRent,
    initialListingRentLowerBound,
    initialListingRentUpperBound,
    hasHomeownerPriceOverride,
    upfrontPlacementFeeAmount,
  } = stepData?.initialPricing || {};

  const isOnboardingPricingFlow =
    stepResource?.report?.subType === 'PricingPostInspection';
  const hasInitialListingRentRange =
    initialListingRentLowerBound && initialListingRentUpperBound;

  const shouldShowInitialListingRentRange =
    hasInitialListingRentRange && FEATURE_FLAGS.INITIAL_LISTING_RENT_RANGE;
  const shouldShowRequestedPrice = hasHomeownerPriceOverride;
  const shouldShowGTTL = isOnboardingPricingFlow;

  const [isPlacementFeeModalOpen, setIsPlacementFeeModalOpen] = useState(false);
  const [isPayNowLoading, setIsPayNowLoading] = useState(false);

  const assignedEmployee = getPricingAssignedEmployee(stepResource);

  const getInitialValues = () => {
    if (stepData) {
      return {
        ...stepData.initialPricing,
        userRequestedInitialRent: stepData.initialPricing?.initialListingRent,
      };
    }

    return {};
  };

  const [formValues, setFormValues] = useState(getInitialValues());

  useEffect(() => {
    setFormValues(getInitialValues());
  }, [stepData]);

  const isBadSentiment = (sentiment) => sentiment === 'Bad';

  const getAcceptedPriceFeedBack = () => {
    if (
      formValues?.userRequestedInitialRent !== initialListingRent ||
      isBadSentiment(formValues?.userInitialPricingSentiment)
    ) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (values) => {
    const getUserRequestedInitialRent = () => {
      if (
        values.userRequestedInitialRent === initialListingRent &&
        !isBadSentiment(values.userInitialPricingSentiment)
      ) {
        return null;
      }
      return values.userRequestedInitialRent;
    };

    const updatedValues = {
      initialPricing: {
        ...values,
        userRequestedInitialRent: getUserRequestedInitialRent(),
        userInitialPricingSentiment: values.userInitialPricingSentiment,
        userFeedbackInitialPricing: isBadSentiment(
          values.userInitialPricingSentiment,
        )
          ? values.userFeedbackInitialPricing
          : null,
        userAcceptedInitialPricing: getAcceptedPriceFeedBack(values),
      },
    };

    await onSaveAndNext(updatedValues);
  };

  if (!stepResource || !stepData) {
    return null;
  }

  return (
    <>
      <PostInspectionContentLayout
        showCityBackground={false}
        stepConfigs={stepConfigs}
        stepConfig={stepConfig}
        title={
          shouldShowRequestedPrice
            ? POST_INSPECTION_FLOW_STRINGS[
                'initial_listing.requested_price_title'
              ]
            : shouldShowInitialListingRentRange
              ? POST_INSPECTION_FLOW_STRINGS[
                  'initial_listing.price_range_title'
                ]
              : POST_INSPECTION_FLOW_STRINGS['initial_listing.title']
        }
        employee={assignedEmployee}
        noOverlay
      >
        <div>
          <Form
            onSubmit={handleSubmit}
            initialValues={getInitialValues()}
            getForm={({ values }) => {
              return (
                <FormLayout>
                  <ConfettiAnimation containerClassName="top-0 left-0 w-full fixed pointer-events-none" />
                  {shouldShowRequestedPrice ? (
                    <Banner
                      tagText={
                        POST_INSPECTION_FLOW_STRINGS[
                          'initial_listing.requested_price_banner.tag'
                        ]
                      }
                      tagColor="navy"
                      variant="gray"
                    >
                      <Text
                        variant="h1"
                        fontWeight="semibold"
                        className="w-full text-center text-h2-xl text-navy md:text-h1-xl"
                      >
                        {formatMoney(initialListingRent)}
                      </Text>

                      <div className="mt-sm flex items-center justify-center gap-2xs">
                        <IconCheckboxSelected className="h-checkbox w-checkbox shrink-0 text-dark-gray" />
                        <Text
                          fontWeight="semibold"
                          variant="p3"
                          className="tracking-widest mr-2xs text-dark-gray"
                        >
                          {
                            POST_INSPECTION_FLOW_STRINGS[
                              'initial_listing.requested_price_banner.auto_adjustments'
                            ]
                          }
                        </Text>
                      </div>

                      <Text className="mt-sm w-full text-left">
                        {
                          POST_INSPECTION_FLOW_STRINGS[
                            'initial_listing.requested_price_banner.benefit'
                          ]
                        }
                      </Text>
                    </Banner>
                  ) : shouldShowInitialListingRentRange ? (
                    <Banner
                      tagText={
                        POST_INSPECTION_FLOW_STRINGS[
                          'initial_listing.price_range_banner.tag'
                        ]
                      }
                      tagColor="navy"
                    >
                      <Text
                        variant="h1"
                        fontWeight="semibold"
                        className="w-full text-center text-h2-xl text-navy md:text-h1-xl"
                      >
                        {`${formatMoney(initialListingRentLowerBound)} - ${formatMoney(initialListingRentUpperBound)}`}
                      </Text>

                      <Text className="mt-sm w-full text-center">
                        {
                          POST_INSPECTION_FLOW_STRINGS[
                            'initial_listing.price_range_banner.benefit'
                          ]
                        }
                      </Text>
                    </Banner>
                  ) : shouldShowGTTL ? (
                    <Banner
                      tagText={
                        POST_INSPECTION_FLOW_STRINGS[
                          'initial_listing.gttl_banner.tag'
                        ]
                      }
                      tagColor="green"
                      className={cx('gttl-banner')}
                    >
                      <div className="object-fit absolute inset-x-0 top-0">
                        <div className={cx('confetti_m_img')}>
                          <Image
                            src="/pricing-flow/confetti_m.svg"
                            alt="Confetti Background"
                          />
                        </div>
                        <div className={cx('confetti_img')}>
                          <Image
                            src="/pricing-flow/confetti_web.svg"
                            alt="Confetti Background"
                          />
                        </div>
                      </div>
                      <Text fontWeight="semibold" className="text-center">
                        {
                          POST_INSPECTION_FLOW_STRINGS[
                            'initial_listing.initial_listing_price'
                          ]
                        }
                      </Text>
                      <Field
                        size="large"
                        name="userRequestedInitialRent"
                        changeAmount={25}
                        min={Math.ceil(initialListingRent * 0.9)}
                        max={initialListingRent}
                        component={AddOrSubtract}
                        onChangeCustom={(value) => {
                          setFormValues({
                            ...formValues,
                            userRequestedInitialRent: value,
                          });
                        }}
                        valueClassName={cx('initial-rent', 'text-green')}
                      />
                      <div className="mx-sm mt-xl grid grid-cols-1 gap-sm md:mx-2xl md:grid-cols-2">
                        {POST_INSPECTION_FLOW_STRINGS[
                          'initial_listing.gttl_banner.benefits'
                        ].map((item, index) => (
                          <div key={index} className="flex flex-row">
                            <IconCheck
                              height={16}
                              width={20}
                              className="mt-2xs text-green"
                            />
                            <Text
                              fontWeight="semibold"
                              className="z-10 ml-xs flex flex-1"
                            >
                              {item}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </Banner>
                  ) : (
                    <div className="flex w-full flex-col items-center justify-center">
                      <Field
                        size="large"
                        name="userRequestedInitialRent"
                        changeAmount={25}
                        min={Math.ceil(initialListingRent * 0.9)}
                        max={initialListingRent}
                        component={AddOrSubtract}
                        onChangeCustom={(value) => {
                          setFormValues({
                            ...formValues,
                            userRequestedInitialRent: value,
                          });
                        }}
                        valueClassName={cx('initial-rent', 'text-navy')}
                      />
                      <Text fontWeight="semibold" className="mt-2xl">
                        {
                          POST_INSPECTION_FLOW_STRINGS[
                            'initial_listing.initial_listing_price'
                          ]
                        }
                      </Text>
                    </div>
                  )}

                  {!shouldShowRequestedPrice &&
                    shouldShowInitialListingRentRange &&
                    shouldShowGTTL && (
                      <div className="mt-2xl flex flex-col items-center gap-sm md:flex-row">
                        <div className={cx('gttl-icon')}>
                          <Image
                            src="/pricing-flow/gttl-icon.svg"
                            alt="Rent Guaranteed"
                            width={60}
                            height={60}
                          />
                        </div>
                        <div>
                          <Text fontWeight="semibold" className="text-navy">
                            {
                              POST_INSPECTION_FLOW_STRINGS[
                                'initial_listing.gttl_title'
                              ]
                            }
                          </Text>
                          <Text className="text-navy">
                            {
                              POST_INSPECTION_FLOW_STRINGS[
                                'initial_listing.gttl_description'
                              ]
                            }
                          </Text>
                        </div>
                      </div>
                    )}

                  {shouldShowRequestedPrice && (
                    <Text className="mt-2xl block text-center text-dark-gray md:hidden">
                      {
                        POST_INSPECTION_FLOW_STRINGS[
                          'initial_listing.requested_price_cta.disclaimer'
                        ]
                      }
                    </Text>
                  )}
                  <div className="mt-2xl hidden flex-col items-center gap-sm md:flex">
                    {shouldShowRequestedPrice && (
                      <Text className="text-center text-dark-gray">
                        {
                          POST_INSPECTION_FLOW_STRINGS[
                            'initial_listing.requested_price_cta.disclaimer'
                          ]
                        }
                      </Text>
                    )}
                    <PostInspectionContentLayout.CtaButton
                      type={shouldShowRequestedPrice ? 'button' : 'submit'}
                      onClick={
                        shouldShowRequestedPrice
                          ? () => setIsPlacementFeeModalOpen(true)
                          : undefined
                      }
                    >
                      {shouldShowRequestedPrice
                        ? POST_INSPECTION_FLOW_STRINGS[
                            'initial_listing.requested_price_cta.button'
                          ]
                        : POST_INSPECTION_FLOW_STRINGS['initial_listing.next']}
                    </PostInspectionContentLayout.CtaButton>
                  </div>
                  {!shouldShowRequestedPrice && (
                    <>
                      <Space value={SPACE_TYPES.XXXL} />
                      <div className="w-full">
                        <Text variant="h3" fontWeight="semibold">
                          {
                            POST_INSPECTION_FLOW_STRINGS[
                              'initial_listing.relationship'
                            ]
                          }
                        </Text>
                      </div>
                      <Satisfaction
                        onChangeCustom={(value) => {
                          setFormValues({
                            ...formValues,
                            userInitialPricingSentiment: value,
                          });
                          setTimeout(() => {
                            scroller.scrollTo('userFeedbackInitialPricing', {
                              duration: 1000,
                              smooth: true,
                            });
                          }, 150);
                        }}
                        fluid
                        validate={() => {}}
                        name="userInitialPricingSentiment"
                      />
                      {values.userInitialPricingSentiment === 'Bad' && (
                        <>
                          <Space value={SPACE_TYPES.XXL} />
                          <TextBubbleWithUserProfile
                            text={
                              shouldShowInitialListingRentRange
                                ? POST_INSPECTION_FLOW_STRINGS[
                                    'initial_listing.price_range_employee_reminder'
                                  ]
                                : POST_INSPECTION_FLOW_STRINGS[
                                    'initial_listing.employee_reminder'
                                  ]
                            }
                            employee={assignedEmployee}
                          />
                          <Space value={SPACE_TYPES.XL} />
                          <Field
                            placeholder={
                              POST_INSPECTION_FLOW_STRINGS[
                                'initial_listing.tell-us-how-you-feel'
                              ]
                            }
                            component={InputFinalFormAdapter}
                            name="userFeedbackInitialPricing"
                          />
                        </>
                      )}
                    </>
                  )}
                  <div className="my-2xl flex w-full md:hidden">
                    <ButtonBase
                      type={shouldShowRequestedPrice ? 'button' : 'submit'}
                      className="rounded h-2xl flex-1 bg-green disabled:bg-gray"
                      onClick={
                        shouldShowRequestedPrice
                          ? () => setIsPlacementFeeModalOpen(true)
                          : undefined
                      }
                    >
                      <Text fontWeight="semibold" className="text-white">
                        {shouldShowRequestedPrice
                          ? POST_INSPECTION_FLOW_STRINGS[
                              'initial_listing.requested_price_cta.button'
                            ]
                          : POST_INSPECTION_FLOW_STRINGS[
                              'initial_listing.next'
                            ]}
                      </Text>
                    </ButtonBase>
                  </div>
                </FormLayout>
              );
            }}
          />
        </div>
      </PostInspectionContentLayout>
      <PlacementFeeModal
        isVisible={isPlacementFeeModalOpen}
        isLoading={isPayNowLoading}
        placementFeeAmount={upfrontPlacementFeeAmount}
        onHide={() => setIsPlacementFeeModalOpen(false)}
        onPayNow={async () => {
          setIsPayNowLoading(true);
          try {
            await handleSubmit(formValues);
            setIsPlacementFeeModalOpen(false);
          } catch {
            dispatch(
              showToast(
                {
                  component: (
                    <Text fontWeight="semibold">
                      {
                        POST_INSPECTION_FLOW_STRINGS[
                          'initial_listing.placement_fee_modal.error'
                        ]
                      }
                    </Text>
                  ),
                  success: false,
                },
                { autoDelete: true, autoDeleteTime: 4000 },
              ),
            );
          } finally {
            setIsPayNowLoading(false);
          }
        }}
      />
    </>
  );
};

InitialListing.propTypes = {
  stepResource: PropTypes.object.isRequired,
  onSaveAndNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  stepData: PropTypes.object.isRequired,
  stepConfigs: PropTypes.arrayOf(PropTypes.object).isRequired,
  stepConfig: PropTypes.object.isRequired,
};
export default InitialListing;
